/**
 * Wealthica Authentication Service
 * 
 * Handles authentication, token management, and validation
 * Extracted from client for focused responsibility
 */

import { supabase } from '@/integrations/supabase/client';
import { ensureValidToken } from '@/services/AuthAwareApiService';

import { logger } from '../utils/logger';
import { 
  WealthicaServiceResult, 
  WEALTHICA_ERROR_CODES, 
  createServiceResult, 
  createErrorResult 
} from '../types/errors';

export type TokenResult = WealthicaServiceResult<string>;

export interface TokenValidationResult {
  valid: boolean;
  reason?: string;
}

export class WealthicaAuthService {
  // Short-lived in-memory token cache (per-browser, non-persistent)
  // Keys use loginName (we pass userId for our flows)
  private static tokenCache: Map<string, { token: string; ts: number }> = new Map();
  private static readonly TOKEN_TTL_MS = 2 * 60 * 1000; // 2 minutes

  private static getCachedToken(loginName: string): string | null {
    try {
      const rec = this.tokenCache.get(loginName);
      if (!rec) return null;
      const fresh = (Date.now() - rec.ts) < this.TOKEN_TTL_MS;
      if (fresh && rec.token && typeof rec.token === 'string' && rec.token.length > 0) {
        logger.debug('WealthicaAuthService', 'Using cached token (short-lived, in-memory)', { loginName: loginName?.slice(0, 8) + '...' });
        return rec.token;
      }
      // Stale -> drop
      this.tokenCache.delete(loginName);
      return null;
    } catch {
      return null;
    }
  }

  private static setCachedToken(loginName: string, token: string) {
    try {
      this.tokenCache.set(loginName, { token, ts: Date.now() });
    } catch { /* noop */ }
  }
  /**
   * Get Wealthica authentication token from Edge Function
   */
  static async getToken(loginName: string): Promise<TokenResult> {
    try {
      // Try in-memory cache first (non-persistent)
      const cached = this.getCachedToken(loginName);
      if (cached) {
        return createServiceResult(true, cached);
      }

      logger.debug('WealthicaAuthService', 'Requesting token from edge function', { loginName });

      // Ensure valid token before making API call
      await ensureValidToken('wealthica2-get-token');

      const { data, error } = await supabase.functions.invoke('wealthica2-get-token', {
        body: { loginName },
      });

      if (error) {
        logger.error('WealthicaAuthService', 'Edge function error', error);
        // Detect 504 Gateway Timeout or timeout-like errors and return clearer message
        const status = (error as any)?.status;
        const msg = (error as any)?.message?.toLowerCase?.() || '';
        const looksLikeTimeout = status === 504 || msg.includes('504') || msg.includes('timeout');
        if (looksLikeTimeout) {
          return createServiceResult(
            false,
            undefined,
            'Wealthica connection timed out (504). Please try again.',
            WEALTHICA_ERROR_CODES.NETWORK_ERROR,
            { status }
          );
        }
        return createServiceResult(false, undefined, `Failed to get token: ${error.message}`, WEALTHICA_ERROR_CODES.API_ERROR);
      }

      if (!data?.token) {
        logger.error('WealthicaAuthService', 'No token returned from edge function');
        return createServiceResult(false, undefined, 'Invalid token response', WEALTHICA_ERROR_CODES.INVALID_DATA);
      }

      logger.debug('WealthicaAuthService', 'Token received successfully');
      this.setCachedToken(loginName, data.token);
      return createServiceResult(true, data.token);
    } catch (error) {
      logger.error('WealthicaAuthService', 'Error getting token', { error });
      // Best-effort timeout detection in thrown exceptions
      const msg = String((error as any)?.message || error || '').toLowerCase();
      const looksLikeTimeout = msg.includes('504') || msg.includes('timeout');
      if (looksLikeTimeout) {
        return createServiceResult(false, undefined, 'Wealthica connection timed out (504). Please try again.', WEALTHICA_ERROR_CODES.NETWORK_ERROR);
      }
      return createErrorResult(error);
    }
  }

  /**
   * Exchange authorization code for bearer token
   */
  static async exchangeCodeForToken(code: string, loginName: string): Promise<TokenResult> {
    try {
      logger.debug('WealthicaAuthService', 'Exchanging code for token', { code });

      // Ensure valid token before making API call
      await ensureValidToken('wealthica2-get-token');

      const { data, error } = await supabase.functions.invoke('wealthica2-get-token', {
        body: { code, loginName },
      });

      if (error) {
        logger.error('WealthicaAuthService', 'Token exchange failed', error);
        const status = (error as any)?.status;
        const msg = (error as any)?.message?.toLowerCase?.() || '';
        const looksLikeTimeout = status === 504 || msg.includes('504') || msg.includes('timeout');
        if (looksLikeTimeout) {
          return createServiceResult(
            false,
            undefined,
            'Wealthica connection timed out (504). Please try again.',
            WEALTHICA_ERROR_CODES.NETWORK_ERROR,
            { status }
          );
        }
        return createServiceResult(false, undefined, `Failed to exchange code: ${error.message}`, WEALTHICA_ERROR_CODES.API_ERROR);
      }

      if (!data?.token) {
        logger.error('WealthicaAuthService', 'No token returned from exchange');
        return createServiceResult(false, undefined, 'Invalid token exchange response', WEALTHICA_ERROR_CODES.INVALID_DATA);
      }

      logger.debug('WealthicaAuthService', 'Token exchange successful');
      this.setCachedToken(loginName, data.token);
      return createServiceResult(true, data.token);
    } catch (error) {
      logger.error('WealthicaAuthService', 'Error exchanging code for token', { error });
      const msg = String((error as any)?.message || error || '').toLowerCase();
      const looksLikeTimeout = msg.includes('504') || msg.includes('timeout');
      if (looksLikeTimeout) {
        return createServiceResult(false, undefined, 'Wealthica connection timed out (504). Please try again.', WEALTHICA_ERROR_CODES.NETWORK_ERROR);
      }
      return createErrorResult(error);
    }
  }

  /**
   * Get token with retry mechanism
   */
  static async getTokenWithRetry(
    code: string, 
    loginName: string, 
    maxRetries: number = 3
  ): Promise<TokenResult> {
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        logger.info('WealthicaAuthService', `Token acquisition attempt ${retryCount + 1}/${maxRetries}`);
        
        const result = await this.exchangeCodeForToken(code, loginName);
        if (result.success && result.data) {
          logger.info('WealthicaAuthService', 'Token obtained successfully', {
            attempt: retryCount + 1
          });
          return result;
        } else {
          logger.warn('WealthicaAuthService', 'Token request unsuccessful', { 
            attempt: retryCount + 1,
            error: result.error 
          });
        }
      } catch (error) {
        logger.error('WealthicaAuthService', 'Token acquisition error', { 
          error,
          attempt: retryCount + 1 
        });
      }
      
      retryCount++;
      
      // Wait before retry (exponential backoff)
      if (retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
        logger.info('WealthicaAuthService', `Waiting ${delay}ms before retry`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    return createServiceResult(false, undefined, 'Failed to obtain token after multiple attempts', WEALTHICA_ERROR_CODES.AUTH_FAILED);
  }

  /**
   * Validate token format and characteristics
   */
  static validateToken(token?: string): TokenValidationResult {
    // Bail early on obviously invalid tokens
    if (!token || typeof token !== 'string' || token.length < 20) {
      return { 
        valid: false, 
        reason: 'Token is missing, too short, or invalid format' 
      };
    }
    
    try {
      // Wealthica tokens are not JWT tokens, so we don't try to decode them
      // Instead, we check for characteristics of Wealthica API access tokens
      
      // Wealthica access tokens typically don't have the "ey" prefix of JWTs
      // If we're seeing JWT format tokens, they're likely ID tokens which won't work
      if (token.startsWith('ey')) {
        logger.warn('WealthicaAuthService', 'Token appears to be a JWT (ID token), not a Wealthica access token');
        return { 
          valid: false, 
          reason: 'Token appears to be JWT format (ID token), not access token' 
        };
      }
      
      // Additional validation: log token pattern for debugging
      const pattern = token.length > 50 ? 
        `${token.substring(0, 5)}...${token.substring(token.length - 5)}` : 
        'too_short';
      
      logger.debug('WealthicaAuthService', 'Token pattern analysis', { 
        length: token.length,
        pattern,
        seemsValid: true
      });
      
      return { valid: true };
    } catch (error) {
      logger.error('WealthicaAuthService', 'Error validating token', { error });
      return { 
        valid: false, 
        reason: 'Error during token validation' 
      };
    }
  }

  /**
   * Store token in storage
   */
  static storeToken(token: string, persistent: boolean = true): void {
    // Server-only token model: no frontend storage
    logger.info('WealthicaAuthService', 'storeToken noop (server-only tokens)', {
      tokenLength: token?.length || 0,
      persistent
    });
  }

  /**
   * Get stored token (abstracted through storage interface)
   */
  static getStoredToken(): string | null {
    // Server-only token model: no frontend storage
    logger.info('WealthicaAuthService', 'getStoredToken noop (server-only tokens)');
    return null;
  }

  /**
   * Clear stored token
   */
  static clearToken(): void {
    // Server-only token model: nothing to clear on frontend
    logger.info('WealthicaAuthService', 'clearToken noop (server-only tokens)');
  }

  /**
   * Check if error is token-related
   */
  static isTokenError(error: unknown): boolean {
    if (!error) return false;
    
    const errorStr = String(error).toLowerCase();
    return errorStr.includes('token') || 
           errorStr.includes('unauthor') || 
           errorStr.includes('auth') ||
           errorStr.includes('expired') ||
           errorStr.includes('invalid');
  }

  /**
   * Check if error is rate limit related
   */
  static isRateLimitError(error: unknown): boolean {
    if (!error) return false;
    
    // Check for common rate limit status codes
    if (typeof error === 'object' && error !== null) {
      if ('status' in error && (error as any).status === 429) return true;
      if ('code' in error && (error as any).code === 'too_many_requests') return true;
    }
    
    // Check for rate limit text in error messages
    const errorStr = String(error).toLowerCase();
    return errorStr.includes('rate limit') || 
           errorStr.includes('too many requests') || 
           errorStr.includes('try again later');
  }
}